/**
 * This file contains all relevant types and snapshot data mapped to the most
 * recent time period. It may or may not be different from what the app
 * currently uses.
 *
 * MSW isn't meant to be arithmetically correct in any form, it's purely meant
 * for UI development.
 *
 * Last updated: 8 May 2023
 */

import { Amount } from "../../types/amount";
import allTokens from "./tokens";

import { Address } from "viem";
import { PermitDomain } from "../../store/global/getAllTokenLists";

// Chain

export enum MswChainRaw {
  ARBITRUM = "arbitrum",
  ETHEREUM = "ethereum",
  POLYGON = "polygon",
}

export type MswChain = `${MswChainRaw}`;

// Tokens

export type MswTokensArbitrum = keyof typeof allTokens.arbitrum.tokens;
export type MswTokensEthereum = keyof typeof allTokens.ethereum.tokens;
export type MswTokensPolygon = keyof typeof allTokens.polygon.tokens;

export type MswToken = MswTokensArbitrum | MswTokensEthereum | MswTokensPolygon;

type MswTokenType = "alt" | "stablecoin" | "major";

export interface MswTokenData {
  name: string;
  ticker: string;
  tokenType: Array<MswTokenType>;
  permitDomain: PermitDomain;
  iconUrl: string;
  colour: string;
  availability: MswTokenAvailability;
  priceUsd: Amount;
  chainInfo: MswChainSupport;
  buy: boolean;
  sell: boolean;
  displayDecimals: number | null;
}

export type MswTokenList = {
  [key in MswToken]?: MswTokenData;
};

export interface MswTokenAvailability {
  isAvailable: boolean;
  canBuy: boolean;
  canSell: boolean;
}

export interface MswChainSupport {
  chainId: number;
  contractAddress: Address;
  decimals: number;
}

export interface MswTokenListResponse {
  tokens: MswTokenList;
  metadata: {
    lastUpdate: number;
  };
}

export function parseFetchTokens(json: MswTokenListResponse) {
  const tokens: Record<string, MswTokenData> = {};
  // Convert prices returned from endpoint to ethers/BigNumber format
  Object.entries(json.tokens).forEach(([key, value]) => {
    const newToken: MswTokenData = { ...value };
    if (value.priceUsd) {
      try {
        newToken.priceUsd = new Amount(value.priceUsd.toString());
        newToken.buy = value.availability.canBuy ?? false;
        newToken.sell = value.availability.canSell ?? false;
      } catch (e) {
        console.warn(
          `Msw: Cannot parse price for ${key} - price provided was ${JSON.stringify(
            value.priceUsd
          )}.`
        );
        newToken.priceUsd = Amount.ZERO;
        newToken.buy = false;
        newToken.sell = false;
      }
    } else if (value) {
      newToken.priceUsd = Amount.ZERO;
      newToken.buy = false;
      newToken.sell = false;
    }
    tokens[key] = newToken;
  });
  return tokens;
}

// Quote

export enum MswPosition {
  Buy = "Buy",
  Sell = "Sell",
}

export enum MswQuoteType {
  OneToOne = "121",
  OneToManyPercentages = "12MPercentages",
  ManyToOne = "M21",
}

export interface MswQuoteSearchParams121 {
  buy_tokens: Array<Address>;
  sell_tokens: Array<Address>;
  sell_amounts: Array<number>;
  taker_address: Address;
  receiver_address: Address;
}

export interface MswQuoteSearchParams12M {
  buy_tokens: Array<Address>;
  sell_tokens: Array<Address>;
  sell_amounts: Array<number>;
  taker_address: Address;
  receiver_address: Address;
  buy_tokens_ratios: Array<number>;
}

export interface MswQuoteSearchParamsM21 {
  buy_tokens: Array<Address>;
  sell_tokens: Array<Address>;
  sell_amounts: Array<number>;
  taker_address: Address;
  receiver_address: Address;
}

export type MswQuoteSearchParams =
  | MswQuoteSearchParams121
  | MswQuoteSearchParams12M
  | MswQuoteSearchParamsM21;

export interface MswQuoteResponse {
  type: `${MswQuoteType}`;
  quoteid: string;
  chainid: number;
  receiver: Address;
  expiry: number;
  buyTokens: MswQuoteTokenFieldResponse;
  sellTokens: MswQuoteTokenFieldResponse;
  gasFee: {
    usd: number;
  };
  toSign: MswMessageResponse;
  error?: string; // Only during development
}

export type MswQuoteTokenFieldResponseValue = {
  amount: string;
  amountUsd: number;
  contractAddress: Address;
  priceUsd: number;
  decimals: number;
  rate?: number;
};

export type MswQuoteTokenFieldResponse = {
  [key in MswToken]?: MswQuoteTokenFieldResponseValue;
};

export interface MswMessageResponse {
  expiry: number;
  taker_address: Address;
  maker_address: Array<Address>;
  maker_nonces: Array<number>;
  taker_tokens: Array<Array<Address>>;
  maker_tokens: Array<Array<Address>>;
  taker_amounts: Array<Array<string>>;
  maker_amounts: Array<Array<string>>;
  receiver: Address;
}

export const MswExpiryOffset = {
  ethereum: 60,
  polygon: 20,
  arbitrum: 20,
};

export const MswGasFee = {
  ethereum: {
    currency: "eth",
    feeNative: 0.016972749963104418,
    feeUsd: 30.882767195366643,
  },
  polygon: {
    currency: "matic",
    feeNative: 0.03711379074494619,
    feeUsd: 0.033371681451714756,
  },
  arbitrum: {
    currency: "eth",
    feeNative: 0.0007410144000000003,
    feeUsd: 1.3496836281600006,
  },
};
